import type { ActionOutcome, DraftAction, Patient, Role, TableState } from '../types.ts';
import { bedCap, emit, eventActive, listOf, seatedRoles, type Ctx } from './common.ts';
import { HANDLERS } from '../reduce.ts';
import { locumPresent } from './act.ts';
import { frozenIntentReason, resolveStage } from './resolve-drafts.ts';

type Candidate = { role: Role; action: DraftAction; patient: Patient };
export const OVERFLOW = { coefficient: 0.25 };

/** Freeze workload exposure when the next playable turn opens. */
export function snapshotOverflow(ctx: Ctx): void {
  for (const t of ctx.s.tables) {
    for (const role of seatedRoles(t)) {
      const list = listOf(ctx.s, t, role);
      const beds = bedCap(ctx.s, t, role);
      const staff = t.seats[role]!.staff;
      const demand = list.reduce((n, p) => n + (role === 'ICU' || p.colour === 'RED' ? 2 : 1), 0);
      const bedRatio = beds > 0 ? Math.max(0, list.length - beds) / beds : list.length > 0 ? 2 : 0;
      const staffRatio = !ctx.s.config.layers.staff ? 0 : staff > 0 ? Math.max(0, demand - staff) / staff : demand > 0 ? 2 : 0;
      const ratio = Math.max(bedRatio, staffRatio);
      t.overflow[role] = { ratio, probability: Math.min(0.5, OVERFLOW.coefficient * ratio), checked: false };
      if (ratio > 0 && t.tableStats.firstOverflowTurn === null) t.tableStats.firstOverflowTurn = ctx.s.clock.turn;
    }
  }
}

/** Allocate public departure costs before selecting an eligible extra stay. */
function departureCandidates(ctx: Ctx, t: TableState, queues: Partial<Record<Role, DraftAction[]>>): Candidate[] {
  const preview = structuredClone(ctx.s);
  const pt = preview.tables.find(table => table.no === t.no)!;
  const roles = seatedRoles(t);
  const offset = (ctx.s.clock.turn - 1) % roles.length;
  const order = [...roles.slice(offset), ...roles.slice(0, offset)];
  const actions = Array.from({ length: 40 }, (_, index) => order.flatMap(role => queues[role]?.[index] ? [{ role, action: queues[role]![index]! }] : [])).flat();
  const candidates: Candidate[] = [];
  for (const { role, action } of actions) {
    if (!('p' in action.cmd) || !action.cmd.p) continue;
    const p = preview.patients[action.cmd.p];
    const h = HANDLERS[action.cmd.type];
    const cost = h.cost(preview, pt, role, action.cmd as never);
    if (!p || frozenIntentReason(preview, action) || cost > pt.seats[role]!.actions.available || h.check(preview, pt, role, action.cmd as never)) continue;
    if ((p.flags.dischargeNotBeforeTurn ?? 0) > ctx.s.clock.turn) continue;
    pt.seats[role]!.actions.available -= cost;
    if (action.cmd.type === 'USE_LOCUM') pt.locums[locumPresent(preview, pt)!.idx]!.actionsLeft--;
    else if (cost === 0) pt.effects.e7FirstDischargeUsed.push(eventActive(preview, 'E7') ? role : p.seat!);
    if (!p.flags.overflowDelayed) candidates.push({ role, action, patient: ctx.s.patients[p.id]! });
    p.status = 'DISCHARGED';
  }
  return candidates;
}

/** Apply at most one seeded, once-per-patient delay to each overloaded service. */
function applyOverflowHolds(ctx: Ctx, t: TableState, candidates: Candidate[]): void {
  for (const role of seatedRoles(t)) {
    const exposure = t.overflow[role];
    const eligible = candidates.filter(c => c.patient.seat === role);
    if (!exposure || exposure.checked || exposure.probability <= 0 || eligible.length === 0) continue;
    exposure.checked = true;
    if (ctx.rng.next() >= exposure.probability) continue;
    const { patient: p } = eligible[ctx.rng.int(eligible.length)]!;
    p.flags.overflowDelayed = true;
    p.flags.dischargeNotBeforeTurn = ctx.s.clock.turn + 1;
    p.attendedTurn = ctx.s.clock.turn;
    t.dashboard.overflowDelays++;
    emit(ctx, { type: 'OVERFLOW_DELAY', table: t.no, seat: role, patient: p.id, severity: 1, text: `${p.name} needs one extra turn because this team is too busy. This discharge costs no action; plan it again next turn.`, audience: ['faculty', `seat:${role}`] });
  }
}

/** Resolve home departures after the bounded workload check. */
export function resolveDepartures(ctx: Ctx, t: TableState, queues: Partial<Record<Role, DraftAction[]>>): ActionOutcome[] {
  applyOverflowHolds(ctx, t, departureCandidates(ctx, t, queues));
  return resolveStage(ctx, t, queues);
}
